import { useMutateExtendReverseTrial } from '@/hooks/mutation/useMutateExtendReverseTrial';
import {
  extendTrialModalBackClicked,
  extendTrialModalExtendClicked,
} from '@/lib/analytics/scribe/ui';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import {
  DEFAULT_TRIAL_EXTENSION_HOURS,
  TRIAL_EXTENSION_HOURS,
} from '@heidi/commerce/contracts';
import { endTrialModalOpenAtom, extendTrialModalOpenAtom } from '@heidi/commerce/data';
import { Button } from '@heidi/design-system/button';
import { FramedIcon } from '@heidi/design-system/framed-icon';
import {
  DialogDrawer,
  DialogDrawerContent,
  DialogDrawerDescription,
  DialogDrawerTitle,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@heidi/design-system/select';
import {
  TypographyH2,
  TypographyP2Bold,
  TypographyPBold,
} from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtom, useSetAtom } from 'jotai';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';

export function ExtendFreeTrialModal() {
  const [isAtomOpen, setIsOpen] = useAtom(extendTrialModalOpenAtom);
  const isOpen = !isAppleCompliantBuild() && isAtomOpen;
  const setEndTrialModalOpen = useSetAtom(endTrialModalOpenAtom);
  const { t } = useTranslations();

  const [selectedHours, setSelectedHours] = useState<string>(
    DEFAULT_TRIAL_EXTENSION_HOURS
  );
  const { mutate: extendTrial, isPending } = useMutateExtendReverseTrial();

  const handleClose = () => {
    extendTrialModalBackClicked({
      selectedHours: Number(selectedHours),
    });
    setIsOpen(false);
    setEndTrialModalOpen(true);
  };

  const handleSuccess = () => {
    setIsOpen(false);
    setEndTrialModalOpen(false);
  };

  const handleExtendTrial = () => {
    extendTrialModalExtendClicked({
      selectedHours: Number(selectedHours),
    });
    extendTrial(Number(selectedHours), {
      onSuccess: handleSuccess,
    });
  };

  return (
    <DialogDrawer
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
        }
      }}
    >
      <DialogDrawerContent className="flex flex-col gap-4 p-6">
        <Button
          size="lg"
          variant="link"
          className="absolute start-0 top-0 flex items-center gap-2 p-2 text-text-secondary"
          onClick={handleClose}
        >
          <ArrowLeft width={14} height={14} className="rtl:-scale-x-100" />
          <TypographyP2Bold>{t('common.back')}</TypographyP2Bold>
        </Button>

        <div className="flex flex-col items-center gap-4">
          <DialogDrawerTitle className="hidden">
            {t('extendFreeTrialModal.needMoreTimeTitle')}
          </DialogDrawerTitle>
          <DialogDrawerDescription className="hidden">
            {t('extendFreeTrialModal.extendTrialFormDesc')}
          </DialogDrawerDescription>

          <FramedIcon state="pro" />
          <div className="flex flex-col items-center">
            <TypographyH2>{t('extendFreeTrialModal.needMoreTimeHeading')}</TypographyH2>
            <TypographyPBold className="text-center text-text-secondary">
              {t('extendFreeTrialModal.lifeIsBusy')}
            </TypographyPBold>
          </div>

          {!isAppleCompliantBuild() && (
            <>
              <div className="flex w-full flex-col gap-y-2">
                <TypographyP2Bold>
                  {t('extendFreeTrialModal.extendForLabel')}
                </TypographyP2Bold>
                <Select
                  value={selectedHours}
                  onValueChange={setSelectedHours}
                  defaultValue={DEFAULT_TRIAL_EXTENSION_HOURS}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t('extendFreeTrialModal.selectDurationPlaceholder')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TRIAL_EXTENSION_HOURS.ONE_HOUR}>
                      {t('extendFreeTrialModal.hours', { count: 1 })}
                    </SelectItem>
                    <SelectItem value={TRIAL_EXTENSION_HOURS.TWO_HOURS}>
                      {t('extendFreeTrialModal.hours_plural', { count: 2 })}
                    </SelectItem>
                    <SelectItem value={TRIAL_EXTENSION_HOURS.FOUR_HOURS}>
                      {t('extendFreeTrialModal.hours_plural', { count: 4 })}
                    </SelectItem>
                    <SelectItem value={TRIAL_EXTENSION_HOURS.SIX_HOURS}>
                      {t('extendFreeTrialModal.hours_plural', { count: 6 })}
                    </SelectItem>
                    <SelectItem value={TRIAL_EXTENSION_HOURS.TWELVE_HOURS}>
                      {t('extendFreeTrialModal.hours_plural', { count: 12 })}
                    </SelectItem>
                    <SelectItem value={TRIAL_EXTENSION_HOURS.TWENTY_FOUR_HOURS}>
                      {t('extendFreeTrialModal.hours_plural', { count: 24 })}
                    </SelectItem>
                    <SelectItem value={TRIAL_EXTENSION_HOURS.FORTY_EIGHT_HOURS}>
                      {t('extendFreeTrialModal.hours_plural', { count: 48 })}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                size="lg"
                className="w-full"
                onClick={handleExtendTrial}
                disabled={isPending}
              >
                {isPending
                  ? t('extendFreeTrialModal.extendingButton')
                  : t('extendFreeTrialModal.extendAccessButton')}
              </Button>
            </>
          )}
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
}
